import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import './RolesPlanning.css';

// ─── Matrice des rôles du Planning Atelier — SUPER-ADMIN SEULEMENT ───────────
//
// Rôles en colonnes, capacités en lignes. Chaque clic écrit immédiatement : il
// n'y a pas de bouton « Enregistrer », donc pas d'état non sauvegardé à perdre.
//
// ⚠️ Cet écran vit dans Administration, PAS dans Planning ▸ Configuration : ce
// dernier est ouvert aux administrateurs du Planning, qui pourraient alors
// s'attribuer des droits. Le serveur refuse aussi (routeur /api/planning-roles),
// l'onglet masqué n'étant qu'une commodité.

export default function RolesPlanning() {
  const [data, setData] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [enCours, setEnCours] = useState(null);   // `${role}|${capacite}` pendant l'écriture
  const [familleOuverte, setFamilleOuverte] = useState(null);

  const charger = useCallback(() => {
    api.get('/planning-roles')
      .then(r => { setData(r.data); setErreur(null); })
      .catch(e => setErreur(e.response?.data?.error || 'Chargement impossible'));
  }, []);
  useEffect(() => { charger(); }, [charger]);

  const basculer = async (roleCode, capacite, valeur) => {
    const cle = `${roleCode}|${capacite}`;
    setEnCours(cle);
    // Écriture optimiste : la case bascule tout de suite, et revient en arrière
    // si le serveur refuse — sans quoi chaque clic attendrait un aller-retour.
    setData(d => ({ ...d, matrice: { ...d.matrice, [roleCode]: { ...d.matrice[roleCode], [capacite]: valeur } } }));
    try {
      await api.patch(`/planning-roles/${roleCode}`, { capacite, autorise: valeur });
      setErreur(null);
    } catch (e) {
      setData(d => ({ ...d, matrice: { ...d.matrice, [roleCode]: { ...d.matrice[roleCode], [capacite]: !valeur } } }));
      setErreur(e.response?.data?.error || 'Enregistrement refusé');
    } finally {
      setEnCours(null);
    }
  };

  if (erreur && !data) return <div className="rp-erreur">⚠️ {erreur}</div>;
  if (!data) return <div className="rp-chargement">Chargement…</div>;

  const { familles, capacites, roles, matrice, porteurs } = data;

  return (
    <div className="rp">
      <p className="rp-intro">
        Ce que <strong>chaque rôle</strong> peut voir et faire dans le Planning Atelier.
        Chaque clic est enregistré immédiatement.
        L'attribution des rôles aux personnes se fait ailleurs&nbsp;:
        <strong> Planning ▸ Configuration ▸ Gestion des accès</strong>.
      </p>
      {erreur && <div className="rp-erreur rp-erreur--ligne">⚠️ {erreur}</div>}

      <div className="table-container rp-defile">
        <table className="rp-table">
          <thead>
            <tr>
              <th className="rp-col-cap">Capacité</th>
              {roles.map(r => (
                <th key={r.code} className="rp-col-role">
                  <span className="rp-role-nom">{r.libelle}</span>
                  <span className="rp-role-compte">
                    {porteurs[r.code] ? `${porteurs[r.code]} compte${porteurs[r.code] > 1 ? 's' : ''}` : 'personne'}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {familles.map(f => {
              const lignes = capacites.filter(c => c.famille === f.code);
              const repliee = familleOuverte !== null && familleOuverte !== f.code;
              return (
                <React.Fragment key={f.code}>
                  <tr className="rp-famille">
                    <td colSpan={roles.length + 1}>
                      <button
                        type="button"
                        className="rp-famille-btn"
                        onClick={() => setFamilleOuverte(familleOuverte === f.code ? null : f.code)}
                        title={repliee ? 'Afficher cette famille seule' : 'Isoler cette famille'}
                      >
                        {f.libelle}
                        <span className="rp-famille-nb">{lignes.length}</span>
                      </button>
                    </td>
                  </tr>
                  {!repliee && lignes.map(c => (
                    <tr key={c.code}>
                      <td className="rp-col-cap">
                        <span className="rp-cap-libelle">{c.libelle}</span>
                        {c.aide && <span className="rp-cap-aide">{c.aide}</span>}
                        <code className="rp-cap-code">{c.code}</code>
                      </td>
                      {roles.map(r => {
                        const systeme = r.code === 'administrateur';
                        const coche = !!matrice[r.code]?.[c.code];
                        return (
                          <td key={r.code} className="rp-cell">
                            <label className={`rp-case${coche ? ' is-on' : ''}${systeme ? ' is-verrou' : ''}`}
                              title={systeme
                                ? "L'Administrateur possède toutes les capacités par construction : c'est la porte de secours si la matrice est mal réglée."
                                : `${c.libelle} — ${r.libelle}`}>
                              <input
                                type="checkbox"
                                checked={coche}
                                disabled={systeme || enCours === `${r.code}|${c.code}`}
                                onChange={(e) => basculer(r.code, c.code, e.target.checked)}
                              />
                              <span aria-hidden="true">{coche ? '✓' : ''}</span>
                            </label>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="rp-note">
        <strong>L'Administrateur possède tout, et ce n'est pas modifiable.</strong> C'est volontaire&nbsp;:
        si la matrice est mal réglée, il reste un rôle capable de la corriger. Le super-administrateur
        du CRM, lui, passe outre en toutes circonstances.
      </p>
    </div>
  );
}
